import { defineRule, configure } from 'vee-validate';
import { numeric, required } from '@vee-validate/rules';

// Tradução das mensagens de erro
const messages = {
    required: 'Campo obrigatório.',
    email: 'Digite um e-mail válido.',
    fullName: 'Digite seu nome completo.',
    phone: 'Digite um número válido.',
    min8: 'O campo deve conter no mínimo 8 caracteres.',
    legalName: 'Digite uma razão social válida',
    cnpj: 'Digite um CNPJ válido.',
    cpf: 'Digite um CPF válido.',
    registryNumber: 'Digite um RG válido',
    zipcode: 'Digite um CEP válido.',
    number: 'Informe um número válido.',
    agency: 'Informe uma agência válida.',
    account: 'Informe uma conta válida.',
    atLeastOneSelected: 'Selecione pelo menos uma opção.',
    url: 'Digite uma URL válida.',
    youtubeUrl: 'Digite uma URL do YouTube válida.',
};

const customMessages = {
    required: (field) => `O campo ${field} é obrigatório.`,
    confirmed: (field) => `Os campos de ${field} não coincidem. Digite novamente.`,
    min: (min) => `O campo deve conter no mínimo ${min} caracteres.`,
    max: (max) => `O campo deve conter no máximo ${max} caracteres.`,
    minLetters: (field) => `O campo ${field} deve conter no mínimo uma letra.`,
    mixedCase: (field) => `O campo ${field} deve conter letras maiúsculas e minúsculas.`,
    numbers: (field) => `O campo ${field} deve conter no mínimo um número.`,
    symbols: (field) => `O campo ${field} deve conter no mínimo um caractere especial.`,
    fileTypes: (types) => `O formato da imagem deve ser ${types.map((t) => t.split('/')[1].toUpperCase()).join(', ')}`,
    fileSize: (size) => `A imagem pode ter até ${size}MB`,
};

// Custom rules
const customRules = {
    atLeastOneSelected: {
        getMessage() {
            return messages.atLeastOneSelected;
        },
        validate(value) {
            return Array.isArray(value) && value.length > 0;
        },
    },
    fullName: {
        getMessage() {
            return messages.fullName;
        },
        validate(value) {
            return !!value.match(/[a-zA-Zà-úÀ-Ú]([-']?[a-zA-Zà-úÀ-Ú]+)*( [a-zA-Zà-úÀ-Ú]([-']?[a-zA-Zà-úÀ-Ú]+)*)/g);
        },
    },
    legalName: {
        getMessage() {
            return messages.legalName;
        },
        validate(value) {
            return !!value.match(/[a-zA-Zà-úÀ-Ú]([-']?[a-zA-Zà-úÀ-Ú]+)*( [a-zA-Zà-úÀ-Ú]([-']?[a-zA-Zà-úÀ-Ú]+)*)/g);
        },
    },
    phone: {
        getMessage() {
            return messages.phone;
        },
        validate(value) {
            const regex = /(\(\d{2}\)\s)(\d{4,5}\-\d{4})/g;
            return !!value.match(regex);
        },
    },
    min8: {
        getMessage() {
            return messages.min8;
        },
        validate(value) {
            return value.length >= 8;
        },
    },
    cpf: {
        getMessage() {
            return messages.cpf;
        },
        validate(value) {
            const regex = /^\d{3}\.\d{3}\.\d{3}\-\d{2}$/;
            return !!value.match(regex);
        },
    },
    cnpj: {
        getMessage() {
            return messages.cnpj;
        },
        validate(value) {
            const regex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/;
            return !!value.match(regex);
        },
    },
    registryNumber: {
        getMessage() {
            return messages.registryNumber;
        },
        validate(value) {
            const regex = /(^\d{1,2}).?(\d{3}).?(\d{3})-?(\d{1}|X|x$)/g;
            return !!value.match(regex);
        },
    },
    zipcode: {
        getMessage() {
            return messages.zipcode;
        },
        validate(value) {
            const regex = /[0-9]{5}-[\d]{3}/g;
            return !!value.match(regex);
        },
    },
    number: {
        getMessage() {
            return messages.number;
        },
        validate(value) {
            const regex = /^[0-9]+$/g;
            return !!value.match(regex);
        },
    },
    agency: {
        getMessage() {
            return messages.number;
        },
        validate(value) {
            const regex = /[0-9]{4}/g;
            return !!value.match(regex);
        },
    },
    account: {
        getMessage() {
            return messages.number;
        },
        validate(value) {
            const regex = /[0-9]{3,12}[0-9a-zA-Z]{1}/g;
            return !!value.match(regex);
        },
    },
    youtubeUrl: {
        getMessage() {
            return messages.youtubeUrl;
        },
        validate(value) {
            if (value === '' || value === null || value === undefined) {
                return true;
            }

            const regex = /^(https:\/\/youtu\.be\/[a-zA-Z0-9_-]{11}|https:\/\/www\.youtube\.com\/watch\?v=[a-zA-Z0-9_-]{11})$/;
            return regex.test(value);
        },
    },
};

export default () => {
    defineRule('required', required);

    defineRule('confirmed', (value, [target, fieldName]) => {
        return value === target ? true : customMessages.confirmed(fieldName);
    });

    defineRule('email', (value) => {
        const regex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return regex.test(value) ? true : messages.email;
    });

    defineRule('password', (value) => {
        const field = 'senha';

        if (value.length < 8) {
            return customMessages.min(field, 8);
        }

        if (!value.match(/[a-zA-Z]/g)) {
            return customMessages.minLetters(field);
        }

        if (!value.match(/[a-z]/g) || !value.match(/[A-Z]/g)) {
            return customMessages.mixedCase(field);
        }

        if (!value.match(/[0-9]/g)) {
            return customMessages.numbers(field);
        }

        if (!value.match(/\p{Z}|\p{S}|\p{P}/u)) {
            return customMessages.symbols(field);
        }

        return true;
    });

    // Vee-validate doesn't treats "false" as a valid value for radio inputs
    defineRule('radioBooleanRequired', (value) => {
        if (value === false || value === true) {
            return true;
        }

        return messages.required;
    });

    defineRule('url', (value) => {
        const regex = /^(https?:\/\/)(localhost|([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/[^\s]*)?$/;

        if (value === '' || value === null || value === undefined) {
            return true;
        }

        return regex.test(value) ? true : messages.url;
    });

    defineRule('max', (value, params) => {
        const maxLength = params[0];

        return value.length < maxLength ? true : customMessages.max(maxLength);
    });

    defineRule('min', (value, params) => {
        const minLength = Number(params[0]);

        return value.length > minLength ? true : customMessages.min(minLength);
    });

    defineRule('type', (file, [allowedTypes = ['image/png', 'image/jpeg']]) => {
        if (!file) return true;

        return allowedTypes.includes(file.type) ? true : 'invalid_type';
    });

    defineRule('fileSize', (file, [maxSizeMB = 2]) => {
        if (!file) return true;

        return file.size <= maxSizeMB * 1024 * 1024 ? true : 'invalid_size';
    });

    defineRule('dimensions', (file, [w, h]) => {
        if (!file) return true;

        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = (e) => {
                const img = new Image();

                img.onload = () => {
                    const isValid = img.width == w && img.height == h;
                    if (!isValid) {
                        resolve('invalid_dimension');
                    } else {
                        resolve(true);
                    }
                };

                img.src = e.target.result;
            };
        });
    });

    defineRule('numeric', numeric);

    Object.entries(customRules).forEach(([name, rule]) => {
        defineRule(name, (value) => {
            return rule.validate(value) ? true : rule.getMessage();
        });
    });

    configure({
        generateMessage: (context) => messages[context.rule.name] || `Campo inválido`,
    });
};
